<template>
  <FriedModal class="stripe-container" @close="handleClose">
    <template #header>
      <FriedModalHeader :header="title" />
    </template>
    <div v-show="showStripe">
      <div class="stripe-info">
        <FriedInput
          class="stripe-input"
          data-external="stripe-company-name-input"
          :disabled="true"
          :label="t('company-name')"
          :model-value="name"
        />
        <FriedInput
          class="stripe-input"
          data-external="stripe-contract-name-input"
          :disabled="true"
          :label="t('contract-name')"
          :model-value="description"
        />
        <FriedInput
          class="stripe-input"
          data-external="stripe-email-input"
          :disabled="true"
          :label="t('email')"
          :model-value="email"
        />
      </div>
      <h4>{{ t('payment-information') }}</h4>
      <StripeElements v-slot="{ elements }" ref="elms" :stripe-key="stripeKey">
        <StripeElement
          ref="card"
          type="card"
          :elements="elements"
          :options="cardOptions"
          class="stripe-card"
          :class="{ focused: stripeFocus }"
          @focus="handleFocus"
          @blur="handleBlur"
          @change="handleChange"
        />
      </StripeElements>
      <div role="alert">{{ error }}</div>
      <h4>{{ value }}</h4>
    </div>
    <div v-if="success" class="success-container">
      <FriedGetAcceptLogo class="success-image" :aria-label="t('logotype')" />
      <span class="success-message">
        {{ t('successfully-payed-with-stripe') }}
      </span>
    </div>
    <template #footer>
      <div v-if="!success" class="footer">
        <FriedButton
          data-external="stripe-cancel-button"
          class="close-button"
          centered="true"
          :button-type="ButtonType.Secondary"
          @click="handleClose"
          >{{ t('cancel') }}
        </FriedButton>
        <FriedButton
          data-external="stripe-submit-button"
          class="success-button"
          :disabled="!complete"
          centered="true"
          :loading="loading"
          @click="submit"
          >{{ t('pay-with-stripe') }}
        </FriedButton>
      </div>
      <div v-else class="footer">
        <FriedButton
          data-external="stripe-done-button"
          class="success-button"
          centered="true"
          @click="handleClose"
        >
          {{ t('done') }}
        </FriedButton>
      </div>
    </template>
  </FriedModal>
</template>

<script lang="ts">
import { ButtonType } from '@getaccept/fried-tofu';
import type { Entity } from '@getaccept/lib-shared-new/src/entity/signing-site/types/entity';
import type { Integration } from '@getaccept/lib-shared-new/src/integrations/types/integration';
import type { SigningPageRecipient } from '@getaccept/lib-shared-new/src/types/signing-page-recipient';
import type { SigningPageDocument } from '@getaccept/lib-shared-new/src/types/signing-page-document';
import { getCurrency, formatCurrency } from '@getaccept/lib-shared-new/src/helpers/currency';
import type { Currency } from '@getaccept/lib-shared-new/src/types/Currency';
import type { PropType } from 'vue';
import { computed, defineComponent, onMounted, ref } from 'vue';
import { StripeElements, StripeElement } from 'vue-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useI18n } from 'vue-i18n';
import type { StripeTokenResponse } from '../../types/stripe-token-response';
import StripeService from '../../services/stripe.service';

export default defineComponent({
  components: {
    StripeElement,
    StripeElements,
  },
  props: {
    recipient: {
      type: Object as PropType<SigningPageRecipient>,
      required: true,
    },
    entity: {
      type: Object as PropType<Entity>,
      required: true,
    },
    document: {
      type: Object as PropType<SigningPageDocument>,
      required: true,
    },
    stripeIntegration: {
      type: Object as PropType<Integration>,
      required: true,
    },
    authId: {
      type: String,
      required: true,
    },
  },
  emits: ['close'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const elms = ref<HTMLElement[]>(null);
    const card = ref<HTMLElement>(null);
    const loading = ref(false);
    const success = ref(false);
    const error = ref('');
    const complete = ref(false);
    const stripeFocus = ref(false);
    const cardOptions = ref({
      style: {
        base: {
          iconColor: '#f2994a',
          color: '#3b3b3b',
        },
      },
    });

    const title = computed(() => {
      if (success.value) {
        return '';
      }
      return t('pay-with-stripe');
    });

    const stripeKey = computed(() => props.stripeIntegration.metaData_1);

    const currency = computed(() => props.entity.currency);

    const amount = computed(() => Math.round(props.document.value * 100));

    const description = computed(() => props.document.name);

    const name = computed(() => props.entity.name);

    const email = computed(() => props.recipient.email);

    const value = computed(() => {
      const currency: Currency = getCurrency(props.entity.currency);
      const value: string = formatCurrency(props.document.value, currency);
      return `${t('amount-to-pay')}: ${value}`;
    });

    const handleClose = () => {
      emit('close');
    };

    const handleFocus = () => {
      stripeFocus.value = true;
    };

    const handleBlur = () => {
      stripeFocus.value = false;
    };

    const submit = async () => {
      const group = elms.value;
      const cardElement = card.value['stripeElement'];
      try {
        const { token } = await group['instance'].createToken(cardElement);
        if (!token) {
          handleFailure();
          return;
        }
        tokenCreated(token);
      } catch (e) {
        handleFailure();
      }
    };

    const tokenCreated = (token: StripeTokenResponse) => {
      loading.value = true;
      if (props.stripeIntegration.hasToken) {
        return reportCharge(token);
      }
      handleSuccess(token);
    };

    const reportCharge = async (token: StripeTokenResponse) => {
      const success: boolean = await StripeService.reportCharge(
        props.document.id,
        props.recipient.id,
        props.authId,
        token.id,
        email.value,
        amount.value
      );
      if (success) {
        return handleSuccess(token);
      }
      return handleFailure();
    };

    const handleChange = (change: { complete: boolean }) => {
      complete.value = change.complete;
    };

    const handleSuccess = (token: StripeTokenResponse) => {
      const { externalUrl } = props.stripeIntegration;
      if (externalUrl) {
        return redirect(externalUrl, token);
      }
      loading.value = false;
      success.value = true;
    };

    const redirect = (url: string, token: StripeTokenResponse) => {
      const redirectUrl = `${url}?token_id=${escape(token.id)}&token_email=${escape(token.email)}`;
      window.location.assign(redirectUrl);
    };

    const handleFailure = () => {
      error.value = t('stripe-payment-error');
      loading.value = false;
    };

    const loadingStripe = ref(true);
    onMounted(() => {
      loadStripe(stripeKey.value).then(() => {
        loadingStripe.value = false;
      });
    });

    const showStripe = computed(() => !success.value && !loading.value && !loadingStripe.value);

    return {
      ButtonType,
      t,
      elms,
      card,
      loading,
      success,
      error,
      complete,
      stripeFocus,
      cardOptions,
      showStripe,
      title,
      stripeKey,
      currency,
      amount,
      description,
      name,
      email,
      value,
      loadingStripe,
      handleClose,
      handleFocus,
      handleBlur,
      submit,
      handleChange,
      tokenCreated,
      reportCharge,
      handleSuccess,
      redirect,
    };
  },
});
</script>

<style lang="scss" scoped>
.stripe-info {
  .stripe-input {
    width: 100%;
    padding: var(--spacing-100) 0;
  }
}

.footer {
  display: flex;
  justify-content: flex-end;

  .close-button {
    margin-right: var(--spacing-50);
  }

  @media only screen and (max-width: $xs) {
    width: 100%;
    flex-direction: column-reverse;

    .close-button {
      width: 100%;
      margin-bottom: var(--spacing-50);
    }

    .success-button {
      width: 100%;
    }
  }
}

.stripe-card {
  border: 1px solid var(--gray-89);
  border-radius: var(--radius-medium);
  transition: border 0.3s;
  background: var(--gray-98);
  height: 40px;
  display: flex;
  align-items: center;
  padding: 0 var(--spacing-100);

  /* stylelint-disable-next-line selector-class-pattern */
  :deep(.StripeElement) {
    width: 100%;
  }

  &.focused {
    border: solid var(--info-dark) 1px;
  }
}

.success-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  margin-top: var(--spacing-200);

  @media only screen and (max-width: $xs) {
    margin-top: 0;
  }

  .success-image {
    width: 8rem;
    height: 8rem;
  }

  .success-message {
    margin-top: var(--spacing-100);
    color: var(--gray-49);
    font-style: italic;
  }
}
</style>
